import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { ChecklistItem, ChecklistStageVM } from '@models/checklist';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { NavigationService } from '@services/nav/nav.service';
import {
	MatterChecklistsDispatch,
	MatterChecklistsState,
} from '@state/matter/checklist';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, take } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
	ProceedModalComponent,
	ProceedModalData,
} from '@components/proceed-modal/proceed-modal.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { LeapDispatch } from 'src/app/public/leap/state/leap.dispatch';
import { TestHooks } from './matter-checklist.hooks';

enum ChecklistDropdownOption {
	SWITCH = 'switch',
	MARK_COMPLETE = 'markComplete',
	EDIT_TEMPLATE = 'editTemplate',
	SEE_ALL = 'seeAll',
}

@UntilDestroy()
@Component({
	selector: 'app-matter-checklist',
	templateUrl: './matter-checklist.component.html',
	styleUrls: ['./matter-checklist.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [openClose()],
	standalone: false,
})
export class MatterChecklistComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public addingTaskTo: ChecklistStageVM | null = null;
	public isCollapsed: { [index: number]: boolean } = {};
	public newTask = '';
	public checklistKebabOptions: DropdownOption<ChecklistDropdownOption>[] = [];

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterChecklistsState.getAll) checklistItems$: Observable<
		ChecklistItem[]
	>;
	@SelectSnapshot(MatterChecklistsState.getAll) checklistItems: ChecklistItem[];
	@Select(MatterChecklistsState.getChecklistTemplateItem)
	template$: Observable<ChecklistItem>;
	@Select(MatterChecklistsState.getStages) stages$: Observable<ChecklistItem[]>;
	@SelectSnapshot(MatterChecklistsState.getStages) _stages: ChecklistItem[];
	@Select(MatterChecklistsState.getChecklistCompletePercentage)
	completePercentage$: Observable<string>;
	@Select(MatterChecklistsState.getSortedStages) _sortedStages$: Observable<
		ChecklistItem[]
	>;

	public sortedStages$: Observable<ChecklistItem[]>;

	@Input() location: 'overview' | 'nav' = 'overview';

	constructor(
		private checklistDispatch: MatterChecklistsDispatch,
		private matDialog: MatDialog,
		private navigationService: NavigationService,
		private analytics: AnalyticsService,
		private leapDispatch: LeapDispatch,
	) {}

	ngOnInit(): void {
		if (this.location === 'nav') {
			this.checklistKebabOptions = [
				{
					name: 'Edit',
					value: ChecklistDropdownOption.EDIT_TEMPLATE,
					icon: ICON.EDIT,
				},
			];
		} else {
			this.checklistKebabOptions = [
				{
					name: 'Switch templates',
					value: ChecklistDropdownOption.SWITCH,
					icon: ICON.REPEAT,
				},
				{
					name: 'Mark all tasks complete',
					value: ChecklistDropdownOption.MARK_COMPLETE,
					icon: ICON.CIRCLE_CHECK,
				},
				{
					name: 'Edit template',
					value: ChecklistDropdownOption.EDIT_TEMPLATE,
					icon: ICON.EDIT,
				},
				{ name: 'See all templates', value: ChecklistDropdownOption.SEE_ALL },
			];
		}

		this.completePercentage$
			.pipe(untilDestroyed(this))
			.subscribe((percentage) => {
				if (percentage === '100') {
					this.checklistKebabOptions = this.checklistKebabOptions.filter(
						(option) => {
							return option.value !== ChecklistDropdownOption.MARK_COMPLETE;
						},
					);
				} else {
					this.checklistKebabOptions = [
						{
							name: 'Switch templates',
							value: ChecklistDropdownOption.SWITCH,
							icon: ICON.REPEAT,
						},
						{
							name: 'Mark all tasks complete',
							value: ChecklistDropdownOption.MARK_COMPLETE,
							icon: ICON.CIRCLE_CHECK,
						},
						{
							name: 'Edit template',
							value: ChecklistDropdownOption.EDIT_TEMPLATE,
							icon: ICON.EDIT,
						},
						{
							name: 'See all templates',
							value: ChecklistDropdownOption.SEE_ALL,
						},
					];
				}
			});

		this.sortedStages$ = this._sortedStages$.pipe(
			untilDestroyed(this),
			map((stages) => {
				// open first incomplete stage of an untouched/pristine checklist
				if (stages.every((s) => s.opened === undefined || s.opened === null)) {
					const clonedStages = structuredClone(stages);
					const earliestIncompleteStage = clonedStages.find((s) => {
						return this.checklistItems.some(
							(stageTask) =>
								stageTask.type === 'task' &&
								stageTask.parentId === s.id &&
								!stageTask.complete,
						);
					});

					if (!earliestIncompleteStage) return stages;

					earliestIncompleteStage.opened = true;
					this.checklistDispatch.updateStageOpenedState({
						id: earliestIncompleteStage.id,
						opened: true,
					});

					return clonedStages;
				}

				return stages;
			}),
		);
	}

	trackByFunction(index: number, item: ChecklistItem): string {
		return `${item.id}-${item.nextNodeId}`;
	}

	toggleStage(index: number): void {
		this.isCollapsed[index] = !this.isCollapsed[index];
	}

	addTask(stage: ChecklistStageVM): void {
		this.addingTaskTo = stage;
	}

	onChecklistOptionSelected(
		option: DropdownResult<DropdownOption<ChecklistDropdownOption>>,
	) {
		switch (option.result.value) {
			case ChecklistDropdownOption.SWITCH:
				this.analytics.logEvent(AnalyticsEvent.CHECKLIST_SWITCH_TEMPLATE);
				this.checklistDispatch.RemoveAll();
				break;
			case ChecklistDropdownOption.MARK_COMPLETE:
				this.analytics.logEvent(AnalyticsEvent.CHECKLIST_MARK_ALL_COMPLETE);
				this.checklistDispatch.toggleAllTasks(true);
				break;
			case ChecklistDropdownOption.EDIT_TEMPLATE:
				this.openProceedModal().subscribe(() => {
					this.template$
						.pipe(untilDestroyed(this), take(1))
						.subscribe((template) => {
							this.navigationService.goToBusinessChecklist(template.id);
						});
				});
				break;
			case ChecklistDropdownOption.SEE_ALL:
				this.navigationService.goToBusinessChecklist();
				break;
		}
	}

	dropStage(event: CdkDragDrop<ChecklistItem[]>) {
		const previousStageIndex = event.previousIndex;
		const currentStageIndex = event.currentIndex;

		if (previousStageIndex === currentStageIndex) return;

		this.sortedStages$
			.pipe(untilDestroyed(this), take(1))
			.subscribe((_stages) => {
				const stages = structuredClone(_stages);
				const stagesToUpdate: ChecklistItem[] = [];

				moveItemInArray(stages, previousStageIndex, currentStageIndex);
				const lastIndex = stages.length - 1;

				stages.forEach((s, index) => {
					let stageRequiresUpdate = false;

					if (index === 0 && !s.headNode) {
						s.headNode = true;
						stageRequiresUpdate = true;
					}

					if (index > 0 && !!s.headNode) {
						s.headNode = false;
						stageRequiresUpdate = true;
					}

					if (index === lastIndex && s.nextNodeId !== '') {
						s.nextNodeId = '';
						stageRequiresUpdate = true;
					}

					const nextStageId = stages.at(index + 1)?.id;
					if (index < lastIndex && s.nextNodeId !== nextStageId) {
						s.nextNodeId = nextStageId;
						stageRequiresUpdate = true;
					}

					if (stageRequiresUpdate) {
						stagesToUpdate.push(s);
					}
				});

				stagesToUpdate.forEach((s) => {
					this.checklistDispatch.Update(s);
				});
			});
	}

	openProceedModal(): Observable<boolean> {
		const data: ProceedModalData = {
			noLabel: 'Cancel',
			primaryText: 'Are you sure you want to edit this checklist?',
			secondaryText:
				'This action will redirect you to the checklist settings page to modify the template.',
			yesLabel: 'Confirm',
		};

		return this.matDialog
			.open(ProceedModalComponent, {
				data,
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((response) => {
					return !!response;
				}),
			);
	}
}
